export const enum RetrievalMode {
    Hybrid = "hybrid",
    Vectors = "vectors",
    Text = "text"
}

export const enum GPT4VInput {
    TextAndImages = "textAndImages",
    Images = "images",
    Texts = "texts"
}

export const enum VectorFields {
    Embedding = "textEmbeddingOnly",
    ImageEmbedding = "imageEmbeddingOnly",
    TextAndImageEmbeddings = "textAndImageEmbeddings"
}

export type ChatAppRequestOverrides = {
    retrieval_mode?: RetrievalMode;
    semantic_ranker?: boolean;
    semantic_captions?: boolean;
    query_rewriting?: boolean;
    reasoning_effort?: string;
    include_category?: string;
    exclude_category?: string;
    seed?: number;
    top?: number;
    max_subqueries?: number;
    results_merge_strategy?: string;
    temperature?: number;
    minimum_search_score?: number;
    minimum_reranker_score?: number;
    prompt_template?: string;
    prompt_template_prefix?: string;
    prompt_template_suffix?: string;
    suggest_followup_questions?: boolean;
    use_oid_security_filter?: boolean;
    use_groups_security_filter?: boolean;
    use_gpt4v?: boolean;
    gpt4v_input?: GPT4VInput;
    vector_fields: VectorFields;
    language: string;
    use_agentic_retrieval: boolean;
    use_document_search?: boolean;
    project_id?: string;
    current_session_id?: string;
    include_full_notes?: boolean;
    disable_generate_pdf_tool?: boolean;
    response_format?: Record<string, unknown>;
    agent_type?: string;
};

export interface SessionFilePayload {
    id: string;
    name: string;
    content_type: string;
    size: number;
    text?: string;
    truncated?: boolean;
    data_url?: string;
    sas_url?: string;
    blob_url?: string;
    blob_path?: string;
    source?: string;
    uploaded_at?: string;
    documentation_type?: string;
    description?: string;
}

export interface ProjectFileMetadata {
    id: string;
    name: string;
    content_type: string;
    size: number;
    uploaded_at: string;
    blob_url?: string;
    blob_path?: string;
    sas_url?: string;
    text?: string;
    truncated?: boolean;
    documentation_type?: string;
    description?: string;
}

export interface UploadFileResponse {
    message?: string;
    status?: string;
    session_file?: SessionFilePayload;
}

export type FileDestinationFilter = "all" | "session" | "database";

export interface FileRecord {
    id: string;
    filename: string;
    content_type: string;
    size: number;
    destination: Exclude<FileDestinationFilter, "all">;
    blob_path?: string | null;
    uploaded_at: number;
    days_remaining: number | null;
    chat_session_id: string | null;
    project_id?: string | null;
    project_name?: string | null;
}

export interface ListFilesResponse {
    files: FileRecord[];
    total: number;
    offset: number;
    limit: number;
}

export type ResponseMessage = {
    content: string;
    role: string;
};

export type Meeting = {
    id: string;
    meetingType: "zoom" | "inperson";
    title: string;
    date: string;
    duration: number;
    transcription?: string;
    transcriptionWordCount: number;
    actionItemsCount: number;
    decisionsCount: number;
    actionItems?: string[];
    decisions?: string[];
    participants?: string[];
    summary?: string;
    timestamp?: number;
};

export type MeetingListResponse = {
    meetings: Meeting[];
    continuation_token?: string;
};

export type ImprovedTranscriptionResponse = {
    originalTranscription: string;
    improvedTranscription: string;
};

export type LifeArea = {
    id: string;
    title: string;
    status: string;
    goal: string;
    interventions: string;
    currentLevel: number;
    goalLevel: number;
    ibicCategory?: string;
    ibicCode?: string;
};

export type CareRuleOutcome = "normal" | "deviation";

export type CareRule = {
    id: string;
    topic: string;
    trigger: string;
    outcome: CareRuleOutcome;
    guidance: string;
    ibicCode: string;
    requiresFollowUp: boolean;
    followUpAction: string;
};

export interface ClientBrief {
    one_line?: string | null;
    identity?: {
        name?: string | null;
        age?: number | null;
        address?: string | null;
        door_code?: string | null;
        needs_interpreter?: boolean | null;
        language?: string | null;
    } | null;
    last_visit?: {
        date?: string | null;
        highlights?: string | null;
        deviations?: string | null;
    } | null;
    current_focus?: {
        goals?: string[] | null;
        approach?: string | null;
        health_notes?: string | null;
    } | null;
    generated_at?: string;
    notes_last_updated?: string | null;
    profile_updated_at?: string | null;
    cached?: boolean;
    stale?: boolean;
}

export type ClientProfile = {
    overview: {
        // Existing fields
        decisionType: string;
        schedule: string;
        consent: string;
        participation: string;
        // Client Information
        personalIdNumber: string;
        maritalStatus: string;
        needsInterpreter: boolean;
        interpreterLanguage: string;
        // Contact Information
        phoneNumber: string;
        email: string;
        preferencesNotes?: string;
        alarmNumber: string;
        // Address
        currentAddress: string;
        registeredAddress: string;
        // Accessibility / Entry
        accessibilityAddress: string;
        accessibilityDirections: string;
        floorNumber: string;
        hasElevator: boolean | null;
        doorCode: string;
        // Legal Representative
        legalRepresentativeName: string;
        legalRepresentativePhone: string;
        legalRepresentativeEmail: string;
        legalRepresentativeRelationship: string;
        legalRepresentativePriority: string;
    };
    background: {
        backgroundInfo: string;
        approach: string;
    };
    factors: {
        personal: string;
        environmental: string;
        health: string;
    };
    lifeAreas: LifeArea[];
    followUp: {
        plan: string;
        history: string;
    };
    careRules: CareRule[];
};

export interface ProfileHistoryVersion {
    id: string;
    version: number;
    timestamp: number;
    edited_by_oid: string;
    edited_by_name: string;
    profile_data: ClientProfile;
    changed_fields: Record<string, { old: any; new: any }>;
}

export interface ProfileHistoryResponse {
    history: ProfileHistoryVersion[];
    created_by_name?: string;
    created_at?: string;
}

export type ResponsibleUser = {
    oid: string;
    display_name: string;
    email: string;
};

export type ProjectAccessOverrides = {
    include_user_ids: string[];
    exclude_user_ids: string[];
};

export type Project = {
    id: string;
    user_oid?: string;
    entra_oid?: string; // Primary owner OID
    created_by_oid?: string | null; // Creator OID (admin who assigned)
    created_by_name?: string; // Creator display name
    name: string;
    created_at: string;
    updated_at: string;
    chat_sessions?: ProjectChatSession[];
    profile?: ClientProfile;
    visibility?: "private" | "company";
    company_id?: string | null;
    shared_at?: string | null;
    shared_by_oid?: string | null;
    location_id?: string | null;
    location_name?: string | null;
    organization_id?: string | null;
    business_area_id?: string | null;
    business_area_name?: string | null;
    unit_id?: string | null;
    unit_name?: string | null;
    organization_link_status?: "linked" | "needs_assignment";
    owner_oid?: string;
    owner_display_name?: string;
    owner_email?: string;
    is_own_project?: boolean;
    can_modify?: boolean;
    responsible_users?: ResponsibleUser[]; // Array of responsible users
    access_overrides?: ProjectAccessOverrides;
};

export type ProjectChatSession = {
    chat_id: string;
    chat_title: string;
    last_message: string;
    created_at: string;
    updated_at: string;
};

export type ProjectListResponse = {
    projects: Project[];
};

export type ProjectChatsResponse = {
    chats: ProjectChatSession[];
};

export type NoteSession = {
    id: string;
    session_id: string;
    title: string;
    last_message: string;
    created_at: string;
    updated_at: string;
};

export type NoteSessionsResponse = {
    sessions: NoteSession[];
};

export type NoteSessionHistoryResponse = {
    id: string;
    entra_oid: string;
    messages: [string, string][]; // [question, answer] pairs
};

export type CreateProjectRequest = {
    name: string;
    location_id?: string | null;
    location_name?: string | null;
    assigned_to_oid?: string; // Legacy: single OID (for backward compat)
    responsible_user_oids?: string[]; // NEW: Array of OIDs of responsible users (admin only)
};

export type AddChatToProjectRequest = {
    chat_id: string;
    chat_title: string;
    last_message: string;
};

export type NoteContent = {
    date: string;
    time: string;
    workflow?: string;
    ibic: string;
    ibic_code?: string;
    note_type: string;
    text: string;
    assessment: string;
    deviation?: string;
};

export type IncidentRootCause = {
    text: string;
    source_note_ids: string[];
    source_profile_sections: string[];
};

export type IncidentPreventiveAction = {
    text: string;
};

export type IncidentFollowUp = {
    suggestion: string;
    recommended_review_in_days: number | null;
};

export type IncidentSource = {
    note_id: string;
    quote: string;
};

export type Incident = {
    deviation_detected: boolean;
    investigation_failed?: boolean;
    error?: string;
    root_causes: IncidentRootCause[];
    preventive_actions: IncidentPreventiveAction[];
    follow_up: IncidentFollowUp;
    sources: IncidentSource[];
};

export type {
    DocumentationCandidate,
    DocumentationDefinitionSnapshot,
    DocumentationDraft,
    DocumentationDraftStatus,
    DocumentationFieldDefinition,
    DocumentationFieldType,
    DocumentationFieldValue,
    DocumentationGroup,
    DocumentationGroupResponse,
    DocumentationProgressEvent,
    DocumentationSourcePolicy,
    DocumentationValidationIssue
} from "../features/documentation/models";

export type WorkflowNodeSnapshot = {
    id: string;
    title?: string;
    description?: string;
};

export type WorkflowStepCompletion = {
    completed_at: number;
    completed_by_oid: string;
    completed_by_name?: string;
    action: "complete" | "reopen";
};

export type WorkflowInstanceStep = {
    step_id: string;
    draft_text: string;
    status: "pending" | "completed";
    completions: WorkflowStepCompletion[];
};

export type WorkflowSubflowSnapshot = {
    id: string;
    title?: string;
    description: string;
    steps: WorkflowNodeSnapshot[];
};

export type WorkflowSubflowInstance = {
    subflow_id: string;
    enabled: boolean;
    steps: WorkflowInstanceStep[];
};

export type WorkflowInstance = {
    schema_version: number;
    workflow_id: string;
    workflow_label: string;
    classified_at: number;
    definition_snapshot: {
        trigger: WorkflowNodeSnapshot;
        steps: WorkflowNodeSnapshot[];
        outcomes: WorkflowNodeSnapshot[];
        subflows?: WorkflowSubflowSnapshot[];
    };
    steps: WorkflowInstanceStep[];
    subflows?: WorkflowSubflowInstance[];
};

export type PatchWorkflowStepRequest = {
    draft_text?: string;
    action?: "complete" | "reopen";
};

export type PatchWorkflowSubflowRequest = {
    enabled: boolean;
};

export type PatchWorkflowSubflowStepRequest = {
    draft_text?: string;
    action?: "complete" | "reopen";
};

export type NoteHistoryVersion = {
    version: number;
    change_type?: string | null;
    change_summary?: string | null;
    title: string;
    content: NoteContent | string;
    timestamp: number;
    edited_by_oid: string;
    edited_by_name: string;
    incident?: Incident | null;
    workflow_instance?: WorkflowInstance | null;
    documentation_metadata?: Record<string, unknown> | null;
    is_signed?: boolean;
    signed_by_name?: string | null;
    signed_by_oid?: string | null;
    signed_at?: number | null;
};

export type NoteHistoryResponse = {
    history: NoteHistoryVersion[];
};

export type Note = {
    id: string;
    project_id: string;
    title: string;
    content: NoteContent | string;
    incident?: Incident | null;
    workflow_instance?: WorkflowInstance | null;
    documentation_metadata?: Record<string, unknown> | null;
    client_brain_ref?: { goal_id: string; goal_title: string } | null;
    timestamp: number;
    created_at: number;
    updated_at: number;
    author_oid?: string;
    author_name?: string;
    current_version?: number;
    last_edited_by_oid?: string;
    last_edited_by_name?: string;
    is_signed?: boolean;
    signed_by_name?: string;
    signed_by_oid?: string;
    signed_at?: number;
    is_struck?: boolean;
    struck_reason?: string;
    struck_by_name?: string;
    struck_by_oid?: string;
    struck_at?: number;
    topic_summary?: NoteTopicSummary;
};

export type NoteTopic = {
    id: string;
    display_label: string;
    source_language: string;
    source: "ai" | "manual";
};

export type NoteTopicStatus = "pending" | "ready" | "failed" | "stale" | "not_generated";

export type NoteTopicSummary = {
    status: NoteTopicStatus;
    effective_topics: NoteTopic[];
};

export type ProjectTopic = {
    id: string;
    display_label: string;
    canonical_label: string;
    aliases: string[];
    count: number;
    last_seen_at: number;
    status: "suggested" | "active" | "rejected";
    match_type?: "semantic";
    score?: number;
};

export type ProjectTopicsResponse = {
    topics: ProjectTopic[];
};

export type NoteTopicOperation =
    | { op: "add_manual"; topic_id: string; source_language?: string; display_label?: string }
    | { op: "remove_manual"; topic_id: string }
    | { op: "reject_ai"; topic_id: string }
    | { op: "create_manual"; label: string; source_language: string };

export type NotesListResponse = {
    notes: Note[];
};

export type ProfileAnalyticsPath = "labeled" | "extraction";

export type AnalyticsOutputIntent = {
    want_table: boolean;
    want_figure: boolean;
};

export type ProfileAnalyticsRequest = {
    query: string;
};

export type ProfileAnalyticsResponse = {
    table: Record<string, string | number>;
    path: ProfileAnalyticsPath;
    note_count: number;
    note_count_before_filter: number;
    output_intent: AnalyticsOutputIntent;
    figure_png_base64?: string;
    figure_error?: string;
};

/** Analytics chart attachment streamed from Intelligence (search agent) chat. */
export type ChatProfileAnalytics = {
    output_intent?: AnalyticsOutputIntent;
    note_count?: number;
    figure_png_base64?: string;
    figure_error?: string;
};

export type NoteSearchResult = Note & {
    project_name?: string;
};

export type NotesSearchResponse = {
    notes: NoteSearchResult[];
    total: number;
};

export type ContentTypeFilter = "all" | "notes" | "chats" | "files";
export type DatePreset = "all" | "today" | "7days" | "30days" | "custom";

export interface SearchFilters {
    contentType: ContentTypeFilter;
    locationId: string | null;
    projectId: string | null;
    datePreset: DatePreset;
    dateFrom?: string;
    dateTo?: string;
}

export type CreateNoteRequest = {
    id?: string;
    title: string;
    content: NoteContent | string;
    incident?: Incident | null;
    workflow_instance?: WorkflowInstance | null;
};

export type ImportedDraft = {
    index: number;
    status: "streaming" | "ready" | "error" | "saving" | "saved" | "save_failed";
    raw_text: string;
    formatted: string;
    hint_date: string | null;
    hint_time: string | null;
    error?: string;
};

export type UpdateNoteRequest = {
    title?: string;
    content?: NoteContent | string;
    incident?: Incident | null;
};

// SignNoteRequest is empty - signer name derived from auth on backend
export type SignNoteRequest = Record<string, never>;

export type GenerateNoteRequest = {
    messages: Array<{
        role: string;
        content: string;
    }>;
};

export type NoteAppRequest = {
    messages: Array<{
        role: string;
        content: string;
    }>;
    session_files?: SessionFilePayload[];
    profile_data?: ClientProfile;
    context: {
        overrides: {
            language?: string;
            project_id?: string;
            current_session_id?: string;
        };
    };
    session_state?: string;
};

export type CreateMeetingRequest = {
    id?: string;
    meetingType: "zoom" | "inperson";
    title: string;
    date?: string;
    duration: number;
    transcription: string;
    actionItems?: string[];
    decisions?: string[];
    participants?: string[];
    summary?: string;
};

export type Thoughts = {
    title: string;
    description: any; // It can be any output from the api
    props?: { [key: string]: any };
};

export type ResponseContext = {
    data_points: string[];
    followup_questions: string[] | null;
    thoughts: Thoughts[];
    sources?: Array<{
        title: string;
        url: string;
        domain: string;
        snippet: string;
    }>;
};

export type ChatAppResponseOrError = {
    message: ResponseMessage;
    delta: ResponseMessage;
    context: ResponseContext;
    session_state: any;
    error?: string;
};

export type ChatAppResponse = {
    message: ResponseMessage;
    delta: ResponseMessage;
    context: ResponseContext;
    session_state: any;
    profile_analytics?: ChatProfileAnalytics;
};

export type ChatAppRequestContext = {
    overrides?: ChatAppRequestOverrides;
};

export type ChatAppRequest = {
    messages: ResponseMessage[];
    is_web_search: boolean;
    use_document_search?: boolean;
    document_files?: string[];
    session_files?: SessionFilePayload[];
    profile_data?: ClientProfile;
    context?: ChatAppRequestContext;
    session_state: any;
};

export type Config = {
    defaultReasoningEffort: string;
    showGPT4VOptions: boolean;
    showSemanticRankerOption: boolean;
    showQueryRewritingOption: boolean;
    showReasoningEffortOption: boolean;
    streamingEnabled: boolean;
    showVectorOption: boolean;
    showUserUpload: boolean;
    showLanguagePicker: boolean;
    showSpeechInput: boolean;
    showSpeechOutputBrowser: boolean;
    showSpeechOutputAzure: boolean;
    showChatHistoryBrowser: boolean;
    showChatHistoryCosmos: boolean;
    showAgenticRetrievalOption: boolean;
    showAdvancedSettings: boolean;
};

export interface WorkHours {
    default_start: string;
    default_end: string;
}

export interface WorkRules {
    // Hard constraints V1 validator implements (each has an enabled toggle)
    rest_between_shifts_hours: number;
    rest_between_shifts_enabled: boolean;
    min_shift_duration_for_rest_rule_hours: number;
    max_continuous_work_hours: number;
    max_continuous_work_enabled: boolean;
    min_weekly_continuous_rest_hours: number;
    weekly_continuous_rest_enabled: boolean;
    // Hard constraints V1 stores; validator V1.5+
    max_weekly_hours: number | null;
    max_monthly_overtime_hours: number | null;
    max_consecutive_work_days: number | null;
    max_night_shift_hours: number | null;
    // Coverage / qualification defaults
    work_hours: WorkHours;
    // Free-text context (LLM scheduler reads, validator ignores)
    legal_rules_source: string;
    soft_preferences: string;
    // Rules the LLM detected but we can't enforce yet (display-only, greyed).
    unsupported_rules: string[];
    // Forward-compat
    employee_preferences: Array<Record<string, unknown>>;
    client_continuity: boolean;
    // Update metadata (server-stamped)
    updated_at?: number;
    updated_by?: string;
}

export interface ReportTheme {
    accent: string;
    heading_color: string;
    title_color: string;
    body_font: string;
    heading_font: string;
    logo_base64: string;
    footer_labels: {
        management: string;
        system: string;
    };
    chart: {
        client: string;
        ref_line: string;
        ref_band: string;
        ref_bar: string;
        pie: string[];
    };
}

export interface Company {
    id: string;
    name: string;
    allowed_domains: string[];
    created_at: number;
    settings: {
        allowed_agents: string[];
        admin_only_agents: string[];
    };
    profile_description?: string;
    profile_updated_at?: number;
    profile_updated_by?: string;
    work_rules?: WorkRules;
    report_theme?: ReportTheme;
}

export interface CompanyMember {
    id: string;
    email: string;
    display_name: string;
    role: "admin" | "member";
    joined_at: number;
}

export interface PlanningMember extends CompanyMember {
    membership_id?: string;
}

export interface CompanySharedDocument {
    filename: string;
    content_type: string;
    size?: number;
    uploaded_at?: string;
    uploaded_by?: string;
    blob_path: string;
}

export interface CompanySharedDocumentsResponse {
    documents: CompanySharedDocument[];
}

export interface UploadCompanySharedDocumentResponse {
    success: boolean;
    document: CompanySharedDocument;
    extraction?: {
        text_extracted?: boolean;
        truncated?: boolean;
        text_length?: number;
        sidecar_blob_path?: string | null;
    };
}

export interface CompanySharedChatPayloadDocument extends SessionFilePayload {
    source: "company_shared";
}

export interface CompanySharedChatPayloadResponse {
    documents: CompanySharedChatPayloadDocument[];
}

export interface PlanningParsedTask {
    task_id: string;
    task_type: string;
    description: string;
    target_employee_id?: string | null;
    target_employee_name?: string | null;
    linked_employee_ids?: string[];
    linked_employee_names?: string[];
    client_id?: string | null;
    client_name?: string | null;
    date?: string | null;
    end_date?: string | null;
    start_time?: string | null;
    end_time?: string | null;
    duration_minutes?: number | null;
    priority: string;
    confidence: number;
    needs_review_reasons: string[];
    supersedes_request_ids?: string[];
}

export interface PlanningRequestPreviewResponse {
    raw_text: string;
    location_id?: string;
    parsed_tasks: PlanningParsedTask[];
    summary: string;
    requires_confirmation: boolean;
    needs_review: boolean;
    intent: "request" | "chat" | "schedule_lookup" | "withdraw" | "unclear";
    can_submit: boolean;
    withdraw_request_ids?: string[];
}

export interface PlanningRequestItem {
    id: string;
    request_id: string;
    company_id: string;
    location_id?: string;
    submitted_by: string;
    submitted_by_name?: string;
    submitted_at: string;
    raw_text: string;
    parsed_tasks: PlanningParsedTask[];
    status: "pending" | "approved" | "rejected";
    reviewed_by?: string | null;
    reviewed_at?: string | null;
    review_note?: string | null;
    auto_approved?: boolean;
    type?: string;
}

export interface PlanningRequestCreateResponse {
    request_id: string;
    status: "pending" | "approved" | "rejected";
    tasks_created: number;
    auto_approved_count?: number;
    scheduled_assignments?: number;
    parsed_tasks: PlanningParsedTask[];
    superseded_request_ids?: string[];
}

export interface PlanningAssignment {
    assignment_id: string;
    logical_assignment_id?: string | null;
    parent_shift_id?: string | null;
    replaces_assignment_id?: string | null;
    source_request_id?: string | null;
    source_task_id?: string | null;
    description?: string | null;
    employee_id: string;
    employee_name: string;
    task_type: string;
    date: string;
    start_time: string;
    end_time: string;
    client_id?: string | null;
    client_name?: string | null;
    location_id?: string | null;
    notes?: string | null;
    reason_codes: string[];
    ai_brief?: string | null;
    schedule_id?: string;
    schedule_start?: string;
    schedule_end?: string;
}

export interface PlanningViolation {
    violation_type: string;
    severity: "error" | "warning";
    message: string;
    related_assignment_id?: string | null;
    related_task_id?: string | null;
}

export interface PlanningUnassignedTask {
    request_id?: string | null;
    task_id?: string | null;
    task_description: string;
    reason: string;
    suggestion?: string | null;
}

export interface PlanningSchedule {
    id: string;
    schedule_id: string;
    company_id: string;
    location_id?: string;
    start_date: string;
    end_date: string;
    status: "draft" | "published" | "archived";
    health: "green" | "yellow" | "red";
    assignments: PlanningAssignment[];
    violations: PlanningViolation[];
    unassigned_tasks: PlanningUnassignedTask[];
    created_by: string;
    created_at: string;
    published_at?: string | null;
    published_by?: string | null;
    ai_explanation?: string | null;
    type?: string;
    revision?: number;
    source_schedule_id?: string | null;
    source_schedule_revision?: number | null;
}

export interface PlanningAssignmentInput {
    assignment_id?: string;
    logical_assignment_id?: string;
    parent_shift_id?: string;
    source_request_id?: string;
    source_task_id?: string;
    employee_id?: string;
    employee_name?: string;
    task_type: string;
    date: string;
    start_time: string;
    end_time: string;
    client_id?: string;
    client_name?: string;
    location_id?: string;
    notes?: string;
    description?: string;
}

export interface PlanningAssignmentSelector {
    assignment_id?: string;
    logical_assignment_id?: string;
    employee_id?: string;
    employee_name?: string;
    date?: string;
    start_time?: string;
    end_time?: string;
    task_type?: string;
}

export type PlanningAssignmentChanges = Partial<Pick<PlanningAssignmentInput,
    "employee_id" | "employee_name" | "task_type" | "date" | "start_time" | "end_time" |
    "client_id" | "client_name" | "location_id" | "notes" | "description" | "parent_shift_id"
>>;

export type PlanningAdminAction = "none" | "approve" | "reject" | "reject_and_generate" |
    "generate" | "publish" | "preview" | "refresh" | "add_assignment" | "edit_assignment" | "delete_assignment";

export interface PlanningAdminChatRequest {
    message: string;
    past_messages?: Array<{ role: "user" | "assistant"; content: string }>;
    rules_context?: string;
    calendar_range?: { start_date: string; end_date: string };
    schedule_id?: string;
    timezone?: string;
}

export interface PlanningAdminChatResponse {
    reply: string;
    action: PlanningAdminAction;
    request_ids?: string[];
    start_date?: string;
    end_date?: string;
    adjustment_instruction?: string;
    fixed_assignments?: PlanningAssignmentInput[];
    add_assignments?: PlanningAssignmentInput[];
    edit_assignment?: PlanningAssignmentSelector & { updates: PlanningAssignmentChanges };
    delete_assignment?: PlanningAssignmentSelector;
}

export interface GeneratePlanningScheduleRequest {
    start_date: string;
    end_date: string;
    location_id?: string;
    constraints?: { prompt?: string; documents?: string[]; fixed_assignments?: PlanningAssignmentInput[] };
    session_id?: string;
    source_draft_id?: string;
    expected_revision?: number;
    previous_draft?: PlanningAssignment[];
    adjustment_instruction?: string;
}

export interface User {
    id: string;
    email: string;
    provider: string;
    provider_user_id?: string | null;
    display_name: string;
    profile_photo_url?: string | null;
    created_at: number;
    last_login: number;
    password_hash?: string | null;
    entra_oid?: string | null;
    company_id?: string | null;
    company_role?: "admin" | "member";
    company_joined_at?: number | null;
    assigned_locations?: string[];
}

export interface Location {
    id: string;
    company_id: string;
    name: string;
    address?: string | null;
    created_at: string;
    updated_at?: string;
    created_by_oid?: string;
}

export interface LocationMember {
    id: string;
    email: string;
    display_name: string;
    role: "admin" | "member";
}

export interface SetLocationMembersResponse {
    success: boolean;
    members_updated: number;
    projects_updated: number;
}

export type SimpleAPIResponse = {
    message?: string;
};

export interface SpeechConfig {
    speechUrls: (string | null)[];
    setSpeechUrls: (urls: (string | null)[]) => void;
    audio: HTMLAudioElement;
    isPlaying: boolean;
    setIsPlaying: (isPlaying: boolean) => void;
}

export type HistoryListApiResponse = {
    sessions: {
        id: string;
        entra_oid: string;
        title: string;
        timestamp: number;
        agent_type?: string;
        match_type?: string;
        match_snippet?: string;
    }[];
    continuation_token?: string;
};

export type HistoryApiResponse = {
    id: string;
    entra_oid: string;
    answers: [string, string][];
};

// ---------------------------------------------------------------------------
// Form templates (AI Draft) — schema shape mirrors Model1TemplateV1
// (ReportGenerationFlow/schemaBridge/templateContractV1.ts)
// ---------------------------------------------------------------------------

export interface FormTableColumn {
    column_name: string;
    description: string;
    type: "string" | "number" | "integer" | "boolean" | "date" | "datetime";
    nullable: boolean;
}

export interface FormFieldDef {
    field_name: string;
    description: string;
    type: "string" | "number" | "integer" | "boolean" | "date" | "datetime" | "array" | "object" | "table";
    required: boolean;
    nullable: boolean;
    enum_values?: string[];
    item_type?: string;
    table_columns?: FormTableColumn[];
    properties?: FormFieldDef[];
}

export interface FormTemplateSchema {
    document_type: string;
    document_description: string;
    schema_version: string;
    extraction_instructions: { overall_goal: string; notes: string[] };
    fields: FormFieldDef[];
}

export interface FormTemplateSummary {
    id: string;
    name: string;
    description?: string;
    business_area_id?: string | null;
    source_file_name?: string;
    created_by_name?: string;
    created_at?: string;
    updated_at?: string;
    version?: number;
}

export interface FormTemplateDoc extends FormTemplateSummary {
    schema: FormTemplateSchema;
}

export type FormFilledValues = Record<string, unknown>;

export interface ParseFormTemplateResponse {
    schema: FormTemplateSchema;
    source_file_name: string;
    text_chars: number;
    truncated: boolean;
}

export interface FillFormTemplateResponse {
    template_id: string;
    project_id: string;
    filled: FormFilledValues;
}
